import { DocumentNode, useQuery } from '@apollo/client'
import { useCallback, useEffect, useState } from 'react'
import useToast from 'shared/components/Toast/useToast'

interface UsePaginationConfig {
    query: DocumentNode // GraphQL query
    variables: Record<string, any> // Initial query variables
    queryPath: string //path to results from the query
    limit: number // Number of items per page
    trackedDivClassName?: string // Class name for the last element to observe
    skip?: boolean // Skip the query if true
    //liveValue?: string //debounced state to track and refetch when it changes, usually for an autocomplete with paginated options
}

export const usePagination = <T>({
    query,
    variables,
    queryPath,
    limit,
    trackedDivClassName,
    skip = false,
}: UsePaginationConfig) => {
    const toast = useToast()
    const [data, setData] = useState<T[]>([])
    const [pageTotal, setPageTotal] = useState(0)
    const [pageNumber, setPageNumber] = useState(0)
    const [isFetching, setIsFetching] = useState(false)
    //This is based on standard format of PaginatedResponses from backend
    const dataPath = `${queryPath}.data`
    const pageInfoPath = `${queryPath}.pageInfo`

    const {
        loading,
        fetchMore,
        refetch: refetchQuery,
    } = useQuery(query, {
        variables: { ...variables, page: 1, limit },
        skip,
        fetchPolicy: 'cache-first',
        onCompleted: (result) => {
            const initialData = getNestedValue(result, dataPath) || []
            const pageInfo = getNestedValue(result, pageInfoPath)

            setData(initialData)
            setPageTotal(pageInfo?.pageTotal || 0)
            setPageNumber(pageInfo?.pageNumber || 1)
        },
        onError: (error) => {
            toast({
                title: 'Error',
                message: 'Unable to fetch data, please try again',
                severity: 'error',
            })
            console.error('Error fetching data:', error)
        },
    })

    const refetch = useCallback(() => {
        refetchQuery({ ...variables, page: pageNumber, limit })
            .then((result) => {
                const initialData = getNestedValue(result.data, dataPath) || []
                const pageInfo = getNestedValue(result.data, pageInfoPath)
                setData(initialData)
                setPageTotal(pageInfo?.pageTotal || 0)
                setPageNumber(pageInfo?.pageNumber || 1)
            })
            .catch((error) => {
                toast({
                    title: 'Error',
                    message: 'Unable to fetch data, please try again',
                    severity: 'error',
                })
                console.error('Error refetching data:', error)
            })
    }, [
        refetchQuery,
        variables,
        pageNumber,
        limit,
        dataPath,
        pageInfoPath,
        toast,
    ])

    const loadMoreData = useCallback(() => {
        if (loading || isFetching || pageNumber >= pageTotal) return

        setIsFetching(true)
        const nextPage = pageNumber + 1

        fetchMore({
            variables: {
                ...variables,
                page: nextPage,
                limit,
            },
        }).then((fetchMoreResult) => {
            const fetchMoreDataArray =
                getNestedValue(fetchMoreResult.data, dataPath) || []
            const updatedData = [...data, ...fetchMoreDataArray]

            const pageInfo = getNestedValue(fetchMoreResult.data, pageInfoPath)
            setPageTotal(pageInfo?.pageTotal || 0)

            setPageNumber(pageInfo?.pageNumber || 1)
            setData(updatedData)
            setIsFetching(false)
        })
    }, [
        variables,
        loading,
        isFetching,
        pageTotal,
        pageNumber,
        fetchMore,
        limit,
        dataPath,
        data,
        pageInfoPath,
    ])

    useEffect(() => {
        if (!trackedDivClassName) return
        if (data.length === 0) return
        const lastElement = document.querySelector(
            `.${trackedDivClassName}:last-child`
        )
        if (!lastElement) return
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                loadMoreData()
            }
        })

        observer.observe(lastElement)

        return () => {
            observer.disconnect()
        }
    }, [data, trackedDivClassName, loadMoreData])

    return {
        data,
        loading,
        isFetching,
        refetch,
        loadMoreData,
        trackedDivClassName,
    }
}

// Helper function to get a nested value from an object using a dot-separated path
const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((acc, key) => acc?.[key], obj)
}
